import { useState } from 'react';
import useNotifications, { type Notification } from '../../../hooks/useNotifications';
import { useAuth } from '../../../contexts/AuthContext';
import AdminNotificationPanel from './AdminNotificationPanel';

const categoryConfig: Record<string, { label: string; color: string; bg: string; icon: string }> = {
  new_service: { label: 'New Service', color: 'text-green-400', bg: 'bg-green-400/10 border-green-400/25', icon: 'ri-flashlight-line' },
  feature:     { label: 'Feature',     color: 'text-ea-blue',  bg: 'bg-ea-blue/10 border-ea-blue/25',   icon: 'ri-star-line' },
  update:      { label: 'Update',      color: 'text-amber-400',bg: 'bg-amber-400/10 border-amber-400/25',icon: 'ri-refresh-line' },
  welcome:     { label: 'Welcome',     color: 'text-purple-400',bg: 'bg-purple-400/10 border-purple-400/25', icon: 'ri-hand-heart-line' },
  offer:       { label: 'Offer',       color: 'text-pink-400', bg: 'bg-pink-400/10 border-pink-400/25',  icon: 'ri-price-tag-3-line' },
};

const fallbackCat = { label: 'Info', color: 'text-ea-gray', bg: 'bg-ea-dark-card border-ea-dark-border', icon: 'ri-information-line' };

const formatDate = (iso: string) => {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const NotificationCard = ({
  notif,
  onMarkRead,
}: {
  notif: Notification;
  onMarkRead: (id: string) => void;
}) => {
  const [expanded, setExpanded] = useState(false);
  const cat = categoryConfig[notif.category] ?? fallbackCat;

  const handleClick = () => {
    setExpanded((p) => !p);
    if (!notif.is_read) onMarkRead(notif.id);
  };

  return (
    <div
      onClick={handleClick}
      className={`group relative rounded-xl border transition-all duration-200 cursor-pointer overflow-hidden ${
        notif.is_read
          ? 'bg-ea-dark-card border-ea-dark-border hover:border-ea-blue/25'
          : 'bg-ea-navy border-ea-blue/30 hover:border-ea-blue/60'
      }`}
    >
      {/* Unread accent bar */}
      {!notif.is_read && (
        <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-ea-blue rounded-l-xl" />
      )}

      <div className="px-5 py-4 flex items-start gap-4">
        {/* Category icon */}
        <div className={`w-9 h-9 flex items-center justify-center rounded-lg border shrink-0 mt-0.5 ${cat.bg}`}>
          <i className={`${cat.icon} ${cat.color} text-base`}></i>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3 mb-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className={`text-sm font-semibold leading-snug ${notif.is_read ? 'text-white/80' : 'text-white'}`}>
                {notif.title}
              </h3>
              {!notif.is_read && (
                <span className="w-2 h-2 rounded-full bg-ea-blue shrink-0 mt-0.5" />
              )}
            </div>
            <span className="text-ea-gray/50 text-[10px] whitespace-nowrap shrink-0">{formatDate(notif.created_at)}</span>
          </div>

          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border mb-2 ${cat.bg} ${cat.color}`}>
            {cat.label}
          </span>

          <p className={`text-xs leading-relaxed transition-all duration-200 ${expanded ? 'text-ea-gray' : 'text-ea-gray/70 line-clamp-2'}`}>
            {notif.body}
          </p>

          <button className="mt-2 text-[10px] text-ea-blue/70 hover:text-ea-blue transition-colors flex items-center gap-1">
            {expanded ? (
              <><i className="ri-arrow-up-s-line"></i> Show less</>
            ) : (
              <><i className="ri-arrow-down-s-line"></i> Read more</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const NotificationsSection = () => {
  const { notifications, loading, unreadCount, markAsRead, markAllAsRead, refetch } = useNotifications();
  const { isAdmin } = useAuth();
  const [filter, setFilter] = useState<'all' | 'unread'>('all');

  // Members only see published notifications
  const visibleNotifications = isAdmin
    ? notifications
    : notifications.filter((n) => n.is_published);

  const filtered = filter === 'unread'
    ? visibleNotifications.filter((n) => !n.is_read)
    : visibleNotifications;

  const displayUnread = visibleNotifications.filter((n) => !n.is_read).length;

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-white font-bold text-xl">Notifications</h2>
          <p className="text-ea-gray text-sm mt-0.5">
            Stay up to date with the latest automations and updates
          </p>
        </div>
        {displayUnread > 0 && (
          <button
            onClick={markAllAsRead}
            className="flex items-center gap-2 px-4 py-2 text-xs font-medium text-ea-blue border border-ea-blue/30 hover:bg-ea-blue/10 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-check-double-line"></i>
            Mark all as read
          </button>
        )}
      </div>

      {/* Admin panel — only shown to admins */}
      {isAdmin && (
        <AdminNotificationPanel notifications={notifications} onRefresh={refetch} />
      )}

      {/* Stats strip */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Total', value: visibleNotifications.length, icon: 'ri-notification-3-line', color: 'text-ea-gray' },
          { label: 'Unread', value: displayUnread, icon: 'ri-mail-unread-line', color: 'text-ea-blue' },
          { label: 'Read', value: visibleNotifications.length - displayUnread, icon: 'ri-check-line', color: 'text-green-400' },
        ].map((s) => (
          <div key={s.label} className="bg-ea-dark-card border border-ea-dark-border rounded-xl px-4 py-3 flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-ea-black border border-ea-dark-border shrink-0">
              <i className={`${s.icon} ${s.color} text-sm`}></i>
            </div>
            <div>
              <p className="text-white font-bold text-lg leading-none">{s.value}</p>
              <p className="text-ea-gray/60 text-[10px] mt-0.5">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filter tabs */}
      <div className="flex items-center gap-1 bg-ea-dark-card border border-ea-dark-border rounded-xl p-1 w-fit">
        {(['all', 'unread'] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-5 py-2 rounded-lg text-xs font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap capitalize ${
              filter === f ? 'bg-ea-blue text-white' : 'text-ea-gray hover:text-white'
            }`}
          >
            {f === 'unread' ? `Unread (${displayUnread})` : 'All'}
          </button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <div className="flex flex-col gap-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 bg-ea-dark-card border border-ea-dark-border rounded-xl animate-pulse" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-16 h-16 flex items-center justify-center rounded-full bg-ea-dark-card border border-ea-dark-border mx-auto mb-4">
            <i className="ri-notification-off-line text-ea-gray/50 text-2xl"></i>
          </div>
          <p className="text-white font-medium text-sm mb-1">
            {filter === 'unread' ? 'All caught up!' : 'No notifications yet'}
          </p>
          <p className="text-ea-gray/50 text-xs">
            {filter === 'unread' ? 'You\'ve read everything. Nice work.' : 'New updates will appear here.'}
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {filtered.map((notif: Notification) => (
            <NotificationCard key={notif.id} notif={notif} onMarkRead={markAsRead} />
          ))}
        </div>
      )}
    </div>
  );
};

export default NotificationsSection;
